import { Card, PageHead, Pill, Loading, ErrorState, StatusDot } from '../ui/primitives';
import { useQuery } from '../ui/useQuery';
import { rsme } from '../lib/rsme';
import { useAiko } from '../aiko/store';

const SUGGESTIONS = [
  'Show me 5T forklifts available next week',
  'Any open repair tickets older than a day?',
  'Draft a quotation for 3T monthly to ABC Logistics',
  'Which certs expire in the next 30 days?',
  'Which leads went quiet in the last 3 days?',
];

export default function Home() {
  const open = useAiko((s) => s.open);
  const ask = useAiko((s) => s.ask);

  const fleet = useQuery(() => rsme.fleet.query({ status: 'all' }), []);
  const repairs = useQuery(() => rsme.repairTickets.list({ status: 'open' }), []);
  const leads = useQuery(() => rsme.leads.list({ status: 'new' }), []);
  const expiring = useQuery(() => rsme.certificates.list({ expiringSoon: true }), []);

  return (
    <div className="max-w-5xl mx-auto">
      <PageHead title="Hey Ian" sub="What needs your eyes today?" />

      <div className="px-5 md:px-8">
        <button
          onClick={() => open()}
          className="w-full text-left rounded-2xl px-5 py-5 glass hover:border-accent/40 transition-colors border border-line group"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-accent grid place-items-center text-bg font-semibold">A</div>
            <div className="min-w-0">
              <div className="font-medium">Ask Aiko</div>
              <div className="text-muted text-sm truncate">she knows every order, asset, repair, lead and rate</div>
            </div>
            <div className="ml-auto text-muted text-xs hidden sm:block">⌘K</div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {SUGGESTIONS.slice(0, 4).map((s) => (
              <span
                key={s}
                onClick={(e) => { e.stopPropagation(); ask(s); }}
                className="text-xs h-7 px-3 inline-flex items-center rounded-full bg-elevated text-muted hover:text-ink hover:bg-line cursor-pointer"
              >
                {s}
              </span>
            ))}
          </div>
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-5 md:px-8 mt-4">
        <KpiCard label="Available" value={fleet.data?.counts.available} tone="good" loading={fleet.loading} error={fleet.error} />
        <KpiCard label="On rent" value={fleet.data?.counts.unavailable} tone="accent" loading={fleet.loading} error={fleet.error} />
        <KpiCard label="In repair" value={fleet.data?.counts.underRepair} tone="warn" loading={fleet.loading} error={fleet.error} />
        <KpiCard label="Open tickets" value={repairs.data?.pagination?.total ?? repairs.data?.data?.length} tone="danger" loading={repairs.loading} error={repairs.error} />
      </div>

      <div className="px-5 md:px-8 mt-6 grid md:grid-cols-2 gap-4">
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="font-medium">Repair tickets to triage</div>
            <span className="text-xs text-muted">{repairs.data?.data?.length ?? 0} open</span>
          </div>
          {repairs.loading && <Loading rows={3} />}
          {repairs.error && <ErrorState error={repairs.error} retry={repairs.refetch} />}
          {repairs.data?.data?.slice(0, 5).map((t) => (
            <div key={t.id} className="flex items-center gap-3 py-2 border-t border-line first:border-0">
              <StatusDot tone={t.priority === 'urgent' || t.priority === 'high' ? 'danger' : t.priority === 'medium' ? 'warn' : 'muted'} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{t.assetName}</div>
                <div className="text-xs text-muted truncate">{t.description}</div>
              </div>
              <Pill tone={t.priority === 'urgent' ? 'danger' : t.priority === 'high' ? 'warn' : 'muted'}>{t.priority ?? '—'}</Pill>
            </div>
          ))}
          {!repairs.loading && !repairs.error && (!repairs.data?.data?.length) && (
            <div className="text-sm text-muted py-3">Nothing open. Workshop is caught up.</div>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="font-medium">New leads</div>
            <span className="text-xs text-muted">{leads.data?.data?.length ?? 0} new</span>
          </div>
          {leads.loading && <Loading rows={3} />}
          {leads.error && <ErrorState error={leads.error} retry={leads.refetch} />}
          {leads.data?.data?.slice(0, 5).map((l) => (
            <div key={l.id} className="flex items-center gap-3 py-2 border-t border-line first:border-0">
              <div className="w-7 h-7 rounded-full bg-elevated grid place-items-center text-xs font-semibold">{(l.name?.[0] ?? '·').toUpperCase()}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{l.name}</div>
                <div className="text-xs text-muted truncate">{l.company ?? l.phone ?? l.source ?? 'unspecified'}</div>
              </div>
              <button onClick={() => ask(`What's the best response for the lead from ${l.name}${l.company ? ` at ${l.company}` : ''}?`)} className="text-xs text-accent hover:underline">draft reply</button>
            </div>
          ))}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="font-medium">Expiring certs (30d)</div>
            <span className="text-xs text-muted">{expiring.data?.data?.length ?? 0}</span>
          </div>
          {expiring.loading && <Loading rows={3} />}
          {expiring.error && <ErrorState error={expiring.error} retry={expiring.refetch} />}
          {expiring.data?.data?.slice(0, 5).map((c) => (
            <div key={c.id} className="flex items-center gap-3 py-2 border-t border-line first:border-0">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{c.assetName}</div>
                <div className="text-xs text-muted truncate">{c.certType} · ends {c.coverageEnd}</div>
              </div>
              <Pill tone="warn">renew</Pill>
            </div>
          ))}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="font-medium">Quick prompts</div>
            <span className="text-xs text-muted">tap to send</span>
          </div>
          <div className="space-y-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => ask(s)} className="block w-full text-left rounded-lg px-3 py-2 text-sm bg-elevated/60 hover:bg-elevated transition-colors">
                {s}
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ label, value, tone, loading, error }: { label: string; value?: number | null; tone: 'good' | 'warn' | 'danger' | 'accent'; loading?: boolean; error?: unknown }) {
  return (
    <Card className="!p-4">
      <div className="text-xs text-muted">{label}</div>
      {loading ? <div className="shimmer h-7 w-16 rounded mt-2" /> : (
        <div className="mt-1 text-2xl font-semibold tabular-nums">{error ? '—' : (value ?? 0)}</div>
      )}
      <div className="mt-2"><Pill tone={tone}>{tone === 'good' ? 'ready' : tone === 'warn' ? 'attention' : tone === 'danger' ? 'urgent' : 'live'}</Pill></div>
    </Card>
  );
}
